import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { API } from "../../types";
import { EntityStatus } from "../../types";
import { formatApiDisplayName } from "../../utils/apiDisplayName";

export interface ApiHubListViewProps {
  apis: API[];
  selectedIds: Set<string>;
  onToggleSelect: (uuid: string) => void;
  onToggleSelectAll: () => void;
  allPageSelected: boolean;
  somePageSelected: boolean;
  onEdit: (api: API) => void;
  onDelete: (api: API) => void;
  onToggleEnabled: (api: API) => void;
  toggleLoadingId: string | null;
}

const ApiHubListView: React.FC<ApiHubListViewProps> = ({
  apis,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allPageSelected,
  somePageSelected,
  onEdit,
  onDelete,
  onToggleEnabled,
  toggleLoadingId,
}) => {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={somePageSelected && !allPageSelected}
                checked={allPageSelected && apis.length > 0}
                onChange={onToggleSelectAll}
                inputProps={{ "aria-label": "Select all on this page" }}
              />
            </TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Spec</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apis.map((api) => {
            const uuid = api.uuid ?? "";
            const selected = uuid ? selectedIds.has(uuid) : false;
            const enabled = api.status === EntityStatus.ACTIVE;

            return (
              <TableRow
                key={uuid || api.name}
                hover
                selected={selected}
                sx={{ "&.Mui-selected": { bgcolor: "action.selected" } }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected}
                    disabled={!uuid}
                    onChange={() => uuid && onToggleSelect(uuid)}
                    inputProps={{
                      "aria-label": `Select ${api.name}`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    title={api.name}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {formatApiDisplayName(api.name)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {api.description?.trim() || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap>
                    {api.version || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {api.specContent ? (
                    <Chip label="Attached" size="small" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={api.status}
                    size="small"
                    color={
                      api.status === EntityStatus.ACTIVE
                        ? "success"
                        : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={enabled}
                        onChange={() => onToggleEnabled(api)}
                        disabled={!uuid || toggleLoadingId === uuid}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(api)}
                      aria-label={`Edit ${api.name}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(api)}
                      disabled={!uuid}
                      aria-label={`Delete ${api.name}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {apis.length === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            No APIs match your filters.
          </Typography>
        </Box>
      )}
    </TableContainer>
  );
};

export default ApiHubListView;
